import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Download, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const ChapterEditor = ({
  selectedBook, loading, onCreateChapter,
  onUpdateChapter, onExportBook, onGenerateCover, generatingCover,
}) => {
  const [showNewChapter, setShowNewChapter] = useState(false);
  const [newChapter, setNewChapter] = useState({ title: "", prompt: "" });
  const [editingChapter, setEditingChapter] = useState(null);

  const handleCreateChapter = async (e) => {
    e.preventDefault();
    if (!newChapter.title) return;
    await onCreateChapter(newChapter);
    setNewChapter({ title: "", prompt: "" });
    setShowNewChapter(false);
  };

  if (!selectedBook) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FileText size={64} className="mx-auto mb-4 opacity-30" />
        <p>Select a book to view and edit</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        {selectedBook.cover_image_url && (
          <div className="mb-4">
            <img
              src={selectedBook.cover_image_url}
              alt="Book Cover"
              className="w-48 h-64 object-cover border border-border"
              data-testid="book-cover-image"
            />
          </div>
        )}

        <h1 className="text-3xl font-bold mb-2">{selectedBook.title}</h1>
        <p className="text-muted-foreground">by {selectedBook.author}</p>

        <div className="flex gap-2 mt-4 flex-wrap">
          <Button
            onClick={onGenerateCover}
            disabled={generatingCover}
            className="bg-purple-600 text-white hover:bg-purple-700"
            data-testid="generate-cover-button"
          >
            {generatingCover ? "Generating..." : "Generate AI Cover"}
          </Button>
          <Button onClick={() => onExportBook("pdf")} variant="outline" className="border-border hover:bg-accent" data-testid="export-pdf-button">
            <Download size={16} className="mr-2" /> PDF
          </Button>
          <Button onClick={() => onExportBook("epub")} variant="outline" className="border-border hover:bg-accent" data-testid="export-epub-button">
            <Download size={16} className="mr-2" /> EPUB
          </Button>
          <Button onClick={() => onExportBook("docx")} variant="outline" className="border-border hover:bg-accent" data-testid="export-docx-button">
            <Download size={16} className="mr-2" /> DOCX
          </Button>
          <Button
            onClick={() => setShowNewChapter(!showNewChapter)}
            className="bg-white text-black hover:bg-gray-200 ml-auto"
            data-testid="new-chapter-button"
          >
            <Plus size={16} className="mr-2" /> New Chapter
          </Button>
        </div>
      </div>

      {showNewChapter && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mb-6 p-4 border border-border bg-surface"
          data-testid="new-chapter-form"
        >
          <form onSubmit={handleCreateChapter} className="space-y-3">
            <Input
              value={newChapter.title}
              onChange={(e) => setNewChapter({ ...newChapter, title: e.target.value })}
              placeholder="Chapter title"
              className="bg-transparent border-border"
              data-testid="chapter-title-input"
            />
            <Textarea
              value={newChapter.prompt}
              onChange={(e) => setNewChapter({ ...newChapter, prompt: e.target.value })}
              placeholder="Optional: Describe what this chapter should be about"
              className="bg-transparent border-border min-h-[80px]"
              data-testid="chapter-prompt-input"
            />
            <Button type="submit" disabled={loading} className="bg-white text-black hover:bg-gray-200" data-testid="create-chapter-button">
              {loading ? "Creating with AI..." : "Create Chapter"}
            </Button>
          </form>
        </motion.div>
      )}

      <div className="space-y-6" data-testid="chapters-list">
        {selectedBook.chapters?.map((chapter, idx) => (
          <div key={chapter.id} className="border border-border p-6 bg-surface" data-testid={`chapter-${chapter.id}`}>
            {editingChapter?.id === chapter.id ? (
              <div className="space-y-3">
                <Input
                  value={editingChapter.title}
                  onChange={(e) => setEditingChapter({ ...editingChapter, title: e.target.value })}
                  className="bg-transparent border-border text-xl font-semibold"
                  data-testid={`edit-chapter-title-${chapter.id}`}
                />
                <Textarea
                  value={editingChapter.content}
                  onChange={(e) => setEditingChapter({ ...editingChapter, content: e.target.value })}
                  className="bg-transparent border-border min-h-[300px] font-mono text-sm"
                  data-testid={`edit-chapter-content-${chapter.id}`}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      onUpdateChapter(chapter.id, { title: editingChapter.title, content: editingChapter.content });
                      setEditingChapter(null);
                    }}
                    className="bg-white text-black hover:bg-gray-200"
                    data-testid={`save-chapter-${chapter.id}`}
                  >
                    Save Changes
                  </Button>
                  <Button onClick={() => setEditingChapter(null)} variant="outline" className="border-border hover:bg-accent" data-testid={`cancel-edit-${chapter.id}`}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-semibold">{idx + 1}. {chapter.title}</h2>
                  <Button
                    onClick={() => setEditingChapter(chapter)}
                    variant="ghost" size="sm" className="hover:bg-accent"
                    data-testid={`edit-chapter-button-${chapter.id}`}
                  >
                    Edit
                  </Button>
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
                  {chapter.content}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
